// Global Settings Context
// Provides shared settings state across all components to ensure immediate propagation of changes

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from './supabase-client';
import { useAuth } from './AuthContext';
import { batchSignUrls, uploadViaEdgeFunction } from './gcs-client';
import { toast } from 'sonner';

// ==================== TYPES ====================

export interface UserSettings {
  user_id: string;
  email_notifications: boolean;
  app_notifications: boolean;

  // Branding - Agency
  watermark_enabled: boolean;
  watermark_logo_url: string | null;
  watermark_position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  watermark_opacity: number;
  watermark_scale: number;

  // Branding - Wysko
  wysko_watermark_enabled: boolean;
  wysko_watermark_position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  wysko_watermark_opacity: number;
  wysko_watermark_scale: number; // 50-200

  email_footer: string;
  default_render_mode: string;
  download_quality: string;
  leri_watermark_enabled: boolean;

  // Billing (Old — deprecated, kept for migration fallback in settings.tsx)
  invoice_company_name?: string | null;
  invoice_nip?: string | null;
  invoice_address?: string | null;

  // Billing (New)
  billing_company_name?: string;
  billing_tax_id?: string;
  billing_street_address?: string;
  billing_postal_code?: string;
  billing_city?: string;
  billing_country?: string;

  // Newsletter
  newsletter_subscribed: boolean;
  resend_contact_id?: string | null;
}

interface SettingsContextType {
  settings: UserSettings | null;
  loading: boolean;
  updateSettings: (updates: Partial<UserSettings>) => Promise<void>;
  refreshSettings: () => Promise<void>;
  uploadFile: (file: File, bucketType: 'avatars' | 'logos') => Promise<string | null>;
}

// ==================== CONTEXT ====================

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// ==================== PROVIDER ====================

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);

  // Load settings from database
  const loadSettings = useCallback(async () => {
    if (!user) {
      setSettings(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch settings
      let { data: settingsData, error: settingsError } = await supabase
        .from('user_settings' as any)
        .select('*')
        .eq('user_id', user.id)
        .single() as { data: UserSettings | null; error: any };

      if (settingsError && settingsError.code === 'PGRST116') {
        // Create settings if missing
        const { data: newSettings, error: createError } = await supabase
          .from('user_settings' as any)
          .insert({ user_id: user.id })
          .select()
          .single() as { data: UserSettings | null; error: any };

        if (createError) throw createError;
        settingsData = newSettings;
      } else if (settingsError) {
        throw settingsError;
      }

      // Sign logo URL if it's a private GCS path
      if (settingsData?.watermark_logo_url && !settingsData.watermark_logo_url.startsWith('http')) {
        try {
          const signedUrls = await batchSignUrls([
            { bucket: 'wysko-assets', path: settingsData.watermark_logo_url }
          ]);

          if (signedUrls[settingsData.watermark_logo_url]) {
            settingsData.watermark_logo_url = signedUrls[settingsData.watermark_logo_url];
          }
        } catch (signError) {
          console.warn('[SettingsContext] Failed to sign logo URL:', signError);
        }
      }

      setSettings(settingsData);
    } catch (error) {
      console.error('[SettingsContext] Error loading settings:', error);
      toast.error('Nie udało się załadować ustawień.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load settings when user changes
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Update settings with optimistic updates
  const updateSettings = useCallback(async (updates: Partial<UserSettings>) => {
    if (!user || !settings) return;

    // Backup only changed fields for rollback
    const changedKeys = Object.keys(updates) as (keyof UserSettings)[];
    const previousValues: Partial<UserSettings> = {};
    changedKeys.forEach(key => {
      previousValues[key] = settings[key];
    });

    // Optimistic update - immediately reflect in UI
    setSettings(prev => prev ? { ...prev, ...updates } : null);

    try {
      console.log('[SettingsContext] Updating settings:', updates);

      const { error } = await supabase
        .from('user_settings' as any)
        .update(updates)
        .eq('user_id', user.id);

      if (error) {
        console.error('[SettingsContext] Supabase error:', error);
        throw error;
      }

      console.log('[SettingsContext] Settings updated successfully');
    } catch (error) {
      console.error('[SettingsContext] Error updating settings:', error);
      // Revert only changed fields
      setSettings(prev => prev ? { ...prev, ...previousValues } : null);
      throw error;
    }
  }, [user, settings]);

  // Upload file to GCS
  const uploadFile = useCallback(async (file: File, bucketType: 'avatars' | 'logos'): Promise<string | null> => {
    if (!user) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      let path = '';

      // Define path structure for wysko-assets bucket
      if (bucketType === 'avatars') {
        path = `avatars/${user.id}/${timestamp}.${fileExt}`;
      } else if (bucketType === 'logos') {
        path = `logos/${user.id}/logo.${fileExt}`; // Consistent path for logo
      }

      // Upload via Edge Function to GCS (Proxy) to avoid CORS
      await uploadViaEdgeFunction(
        file,
        'wysko-assets',
        user.id,
        undefined, // propertyId not needed for assets
        path // customPath
      );

      return path; // Return the path for DB
    } catch (error) {
      console.error('[SettingsContext] Error uploading file:', error);
      toast.error('Błąd przesyłania pliku');
      return null;
    }
  }, [user]);

  const value: SettingsContextType = {
    settings,
    loading,
    updateSettings,
    refreshSettings: loadSettings,
    uploadFile,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

// ==================== HOOK ====================

export function useSettingsContext() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettingsContext must be used within a SettingsProvider');
  }
  return context;
}
